import React, { useState, useEffect } from 'react';
import { useBed } from '../../../context/BedContext';

const BedCard = ({ bed, onEdit, onView, onDelete, onStatusChange }) => {
  const { getEquipmentUsingBed } = useBed();
  const [equipment, setEquipment] = useState([]);
  const [loadingEquipment, setLoadingEquipment] = useState(false);

  useEffect(() => {
    if (bed.status === 'IN_USE') {
      loadEquipment();
    }
  }, [bed.id, bed.status]);

  const loadEquipment = async () => {
    setLoadingEquipment(true);
    try {
      const eq = await getEquipmentUsingBed(bed.id);
      setEquipment(eq);
    } catch (err) {
      console.error('Error loading equipment:', err);
    } finally {
      setLoadingEquipment(false);
    }
  };

  const getStatusBadge = () => {
    switch (bed.status) {
      case 'AVAILABLE':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">🟢 ว่าง</span>;
      case 'IN_USE':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">🔴 ใช้งานอยู่</span>;
      case 'MAINTENANCE':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">🟡 ซ่อมบำรุง</span>;
      case 'UNAVAILABLE':
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">⚫ ไม่พร้อมใช้งาน</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{bed.status}</span>;
    }
  };

  const getStatusColor = () => {
    switch (bed.status) {
      case 'AVAILABLE':
        return 'border-green-300 bg-green-50';
      case 'IN_USE':
        return 'border-red-300 bg-red-50';
      case 'MAINTENANCE':
        return 'border-yellow-300 bg-yellow-50';
      case 'UNAVAILABLE':
        return 'border-gray-300 bg-gray-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  return (
    <div className={`p-4 rounded-lg border-2 shadow-sm hover:shadow-md transition ${getStatusColor()}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-800">{bed.bedName}</h3>
          <p className="text-xs text-gray-500">#{bed.bedNumber}</p>
        </div>
        {getStatusBadge()}
      </div>

      {/* Equipment List (แสดงเฉพาะเมื่อใช้งานอยู่) */}
      {bed.status === 'IN_USE' && (
        <div className="mb-3">
          {loadingEquipment ? (
            <div className="text-xs text-gray-500">กำลังโหลด...</div>
          ) : equipment.length > 0 ? (
            <div className="space-y-1">
              {equipment.slice(0, 2).map((eq, index) => (
                <div key={index} className="text-xs text-gray-700">
                  📦 {eq.name || 'ไม่ระบุชื่อ'}
                </div>
              ))}
              {equipment.length > 2 && (
                <div className="text-xs text-gray-500">
                  +{equipment.length - 2} อื่นๆ
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">ไม่มีอุปกรณ์</div>
          )}
        </div>
      )}

      {/* Ward & Department */}
      <div className="mb-3 space-y-1">
        <p className="text-xs text-gray-600">{bed.ward}</p>
        <p className="text-xs text-gray-600">{bed.department}</p>
        {bed.bedLocation && (
          <p className="text-xs text-gray-500">{bed.bedLocation}</p>
        )}
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mt-4">
        {onEdit && (
          <button
            onClick={() => onEdit(bed)}
            className="flex-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition"
          >
            ✏️ แก้ไข
          </button>
        )}
        {onView && (
          <button
            onClick={() => onView(bed)}
            className="flex-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
          >
            📋 ดู
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => {
              if (window.confirm(`ต้องการลบเตียง "${bed.bedName}" (#${bed.bedNumber}) หรือไม่?`)) {
                onDelete(bed);
              }
            }}
            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition"
            title="ลบเตียง"
          >
            🗑️ ลบ
          </button>
        )}
        {bed.status === 'IN_USE' && onStatusChange && (
          <button
            onClick={() => onStatusChange(bed)}
            className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition"
            title="เปลี่ยนสถานะ"
          >
            🔄
          </button>
        )}
      </div>
    </div>
  );
};

export default BedCard;

